<script>
import { mapState } from "vuex";
import Layout from "@/router/layouts/main";
import EmptyState from "@/components/widgets/empty-state";
import { api } from "@/state/services";
import { canAccess } from "@/utils/menu-map";
import {
  TRILHAS,
  INDICADORES,
  EMPRESAS,
  MESES,
  MEDALHAS,
  fmtPontos,
  fmtPeso,
  fmtPeriodo,
  fmtAtualizadoEm,
  iniciais,
} from "@/utils/ranking";

// Enquanto o primeiro cálculo do período roda no servidor, a API responde 202 "processing".
// O interceptor global (state/services/api.service.js) NÃO repete chamadas — e não deve ganhar
// esse comportamento só por causa desta tela —, então o polling mora aqui.
const POLL_INTERVALO_MS = 4000;
const POLL_MAX_TENTATIVAS = 45; // ~3 minutos

/**
 * Tela única das 4 trilhas do novo modelo de reconhecimento (TOP 20 Consultores, Home Office,
 * Executiva e Gerentes). A trilha vem de `meta.trilha` na rota, como no biTeams.
 *
 * Diferente do biTeams: a API já devolve o recorte pronto (TOP 20 + a linha de quem está logado),
 * nunca a lista inteira; não há filtro de regional nem exportação; e a tela é pensada primeiro
 * para o celular (cards abaixo de md, tabela só no desktop).
 */
export default {
  components: { Layout, EmptyState },
  data() {
    return {
      EMPRESAS,
      MEDALHAS,
      empresa: "brasil",
      ano: null,
      mes: null,
      trimestre: null,
      limites: null,
      dados: null,
      carregando: false,
      processando: false,
      erro: "",
    };
  },
  computed: {
    ...mapState({ account: (state) => state.account }),
    trilha() {
      return TRILHAS[this.$route.meta.trilha] || TRILHAS.consultores;
    },
    colunas() {
      return this.trilha.pesos.map((p) => ({ key: p.key, peso: p.peso, ...INDICADORES[p.key] }));
    },
    mostraRegional() {
      return this.trilha.id === "consultores";
    },
    podeVerRegras() {
      return canAccess(this.account.user, "/biteams/rk-regras");
    },
    top() {
      return (this.dados && this.dados.top) || [];
    },
    podio() {
      return this.top.slice(0, 3);
    },
    eu() {
      return this.dados ? this.dados.eu : null;
    },
    periodoLabel() {
      return this.dados ? fmtPeriodo(this.dados.periodo) : "";
    },
    anos() {
      if (!this.limites) return [];
      const lista = [];
      for (let a = this.limites.ano_atual; a >= this.limites.ano_minimo; a--) lista.push(a);
      return lista;
    },
    meses() {
      if (!this.limites) return [];
      const max = this.ano === this.limites.ano_atual ? this.limites.mes_atual : 12;
      return MESES.slice(0, max).map((text, i) => ({ value: i + 1, text }));
    },
    trimestres() {
      if (!this.limites) return [];
      const max = this.ano === this.limites.ano_atual ? this.limites.trimestre_atual : 4;
      const lista = [];
      for (let t = 1; t <= max; t++) lista.push({ value: t, text: `${t}º trimestre` });
      return lista;
    },
    euPontuou() {
      return !!(this.eu && this.eu.participa && this.eu.pontos > 0);
    },
    // Frase de "quanto falta" do bloco Sua posição. Empate aparece como empate, e não como
    // "faltam 0 pontos" — com posições distintas para pontos iguais, é o que realmente acontece.
    mensagemProgresso() {
      const eu = this.eu;
      if (!eu || !eu.participa) return "";
      if (!this.euPontuou) {
        return "Você ainda não pontuou neste período. Assim que pontuar, sua posição aparece aqui.";
      }
      if (eu.posicao === 1) return "Você lidera o ranking. Continue assim!";

      const premiados = this.dados.premiados;
      const topN = this.dados.top_n;
      const acima = eu.posicao - 1;
      if (eu.premiado) {
        return this.textoFalta(eu.pontos_para_subir, `para alcançar o ${acima}º colocado`, `com o ${acima}º colocado`);
      }
      if (eu.no_top) {
        return this.textoFalta(
          eu.pontos_para_premiacao,
          `para alcançar a zona de premiação (${premiados}º colocado)`,
          `com o ${premiados}º colocado, o último da zona de premiação`
        );
      }
      if (eu.pontos_para_top !== null) {
        return this.textoFalta(eu.pontos_para_top, `para entrar no TOP ${topN}`, `com o ${topN}º colocado`);
      }
      return "";
    },
  },
  watch: {
    // Trocar de trilha pelo menu reaproveita o componente: volta ao período corrente.
    "$route.meta.trilha"() {
      this.dados = null;
      this.limites = null;
      this.ano = null;
      this.mes = null;
      this.trimestre = null;
      this.carregar();
    },
  },
  created() {
    this.seq = 0;
    this.timer = null;
    this.tentativas = 0;
    this.carregar();
  },
  beforeDestroy() {
    this.cancelarPolling();
  },
  methods: {
    fmtPontos,
    fmtPeso,
    fmtAtualizadoEm,
    iniciais,

    textoFalta(pontos, acao, empate) {
      const n = Number(pontos) || 0;
      if (n <= 0) return `Você está empatado em pontos ${empate}.`;
      return n <= 1
        ? `Falta ${fmtPontos(n)} ponto ${acao}.`
        : `Faltam ${fmtPontos(n)} pontos ${acao}.`;
    },
    classePodio(posicao) {
      return posicao <= 3 ? `is-pos-${posicao}` : "";
    },
    cancelarPolling() {
      if (this.timer) {
        clearTimeout(this.timer);
        this.timer = null;
      }
    },
    montarQuery() {
      const params = new URLSearchParams();
      params.set("empresa", this.empresa);
      if (this.ano) {
        params.set("ano", this.ano);
        if (this.trilha.trimestral && this.trimestre) params.set("trimestre", this.trimestre);
        if (!this.trilha.trimestral && this.mes) params.set("mes", this.mes);
      }
      return params.toString();
    },
    // `polling` = nova tentativa automática de um cálculo em andamento (mantém o contador).
    async carregar(polling = false) {
      this.cancelarPolling();
      if (!polling) this.tentativas = 0;

      // Troca de filtro no meio de uma requisição: só a resposta mais recente vale.
      const seq = ++this.seq;
      this.carregando = true;
      this.erro = "";
      if (!polling) this.processando = false;

      try {
        const res = await api.get(`${this.trilha.endpoint}?${this.montarQuery()}`);
        if (seq !== this.seq) return;

        const corpo = res.data || {};
        if (res.status === 202 || corpo.status === "processing") {
          if (this.tentativas >= POLL_MAX_TENTATIVAS) {
            this.processando = false;
            this.carregando = false;
            this.erro = "O cálculo está demorando mais que o normal. Tente novamente em alguns minutos.";
            return;
          }
          this.tentativas++;
          this.processando = true;
          this.timer = setTimeout(() => this.carregar(true), POLL_INTERVALO_MS);
          return;
        }

        if (corpo.status === "success") {
          this.aplicar(corpo);
        } else {
          this.erro = corpo.message || "Não foi possível carregar o ranking agora.";
        }
        this.processando = false;
        this.carregando = false;
      } catch (e) {
        if (seq !== this.seq) return;
        const msg = e && e.response && e.response.data && e.response.data.message;
        this.erro = msg || "Não foi possível carregar o ranking. Verifique sua conexão e tente novamente.";
        this.processando = false;
        this.carregando = false;
      }
    },
    aplicar(corpo) {
      this.dados = corpo;
      this.limites = corpo.limites;
      this.empresa = corpo.empresa;
      this.ano = corpo.periodo.ano;
      if (this.trilha.trimestral) this.trimestre = corpo.periodo.trimestre;
      else this.mes = corpo.periodo.mes;
    },
    onPeriodo() {
      this.carregar();
    },
    onAno(valor) {
      this.ano = valor;
      // Voltar para o ano corrente com um mês/trimestre que ainda não começou: ajusta para o último válido.
      if (this.limites && valor === this.limites.ano_atual) {
        if (this.mes > this.limites.mes_atual) this.mes = this.limites.mes_atual;
        if (this.trimestre > this.limites.trimestre_atual) this.trimestre = this.limites.trimestre_atual;
      }
      this.carregar();
    },
    onEmpresa(valor) {
      this.empresa = valor;
      this.carregar();
    },
  },
};
</script>

<template>
  <Layout>
    <div class="rk-page">
      <ol class="breadcrumb">
        <li class="breadcrumb-item">
          <router-link tag="a" to="/" aria-label="Início">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
              <path d="M5 22h14a2 2 0 0 0 2-2v-9a1 1 0 0 0-.29-.71l-8-8a1 1 0 0 0-1.41 0l-8 8A1 1 0 0 0 3 11v9a2 2 0 0 0 2 2zm5-2v-5h4v5zm-5-8.59 7-7 7 7V20h-3v-5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v5H5z"></path>
            </svg>
          </router-link>
        </li>
        <li class="breadcrumb-item">Rankings</li>
        <li class="breadcrumb-item d-none d-sm-block">{{ trilha.menuLabel }}</li>
      </ol>

      <div class="page-title-box mb-3">
        <h3 class="mb-1">{{ trilha.titulo }}</h3>
        <p class="rk-subtitulo mb-2">{{ trilha.subtitulo }}</p>
        <div class="rk-tags">
          <span class="rk-tag">{{ trilha.periodicidade }}</span>
          <span class="rk-tag rk-tag-premio"><i class="bx bx-trophy" aria-hidden="true"></i> {{ trilha.premiacao }}</span>
          <router-link v-if="podeVerRegras" to="/biteams/rk-regras" class="rk-link-regras">
            Ver regras <i class="bx bx-chevron-right" aria-hidden="true"></i>
          </router-link>
        </div>
      </div>

      <!-- Filtros: só aparecem depois da 1ª resposta, que traz o período corrente e os limites
           (calculados no fuso de Brasília pelo servidor — o relógio do aparelho não decide). -->
      <div v-if="limites" class="card rk-filtros">
        <div class="card-body">
          <div class="rk-filtros-grid">
            <div v-if="trilha.trimestral" class="rk-campo">
              <label for="rk-trimestre" class="rk-label">Trimestre</label>
              <b-form-select id="rk-trimestre" v-model="trimestre" :options="trimestres" class="rk-select" @change="onPeriodo" />
            </div>
            <div v-else class="rk-campo">
              <label for="rk-mes" class="rk-label">Mês</label>
              <b-form-select id="rk-mes" v-model="mes" :options="meses" class="rk-select" @change="onPeriodo" />
            </div>
            <div class="rk-campo">
              <label for="rk-ano" class="rk-label">Ano</label>
              <b-form-select id="rk-ano" :value="ano" :options="anos" class="rk-select" @change="onAno" />
            </div>
            <div class="rk-campo rk-campo-empresa">
              <span id="rk-empresa-label" class="rk-label">Empresa</span>
              <b-form-radio-group
                :checked="empresa"
                :options="EMPRESAS"
                buttons
                button-variant="outline-primary"
                class="rk-segmentado"
                aria-labelledby="rk-empresa-label"
                @change="onEmpresa"
              />
            </div>
          </div>
        </div>
      </div>

      <!-- Carregando / calculando -->
      <div v-if="carregando" class="card">
        <div class="card-body rk-carregando" role="status" aria-live="polite">
          <b-spinner variant="primary" />
          <div class="rk-carregando-titulo">
            {{ processando ? "Calculando o ranking deste período…" : "Carregando ranking…" }}
          </div>
          <div v-if="processando" class="rk-carregando-sub">
            Na primeira consulta do período isso pode levar alguns segundos. A tela atualiza sozinha.
          </div>
        </div>
      </div>

      <!-- Erro -->
      <div v-else-if="erro" class="card" role="alert">
        <EmptyState icon="bx bx-error-circle" title="Não foi possível carregar o ranking" :text="erro">
          <template #action>
            <b-button variant="default" @click="carregar()">Tentar novamente</b-button>
          </template>
        </EmptyState>
      </div>

      <template v-else-if="dados">
        <!-- Estado do período -->
        <div class="rk-status" :class="dados.fechado ? 'is-fechado' : 'is-vivo'">
          <i :class="dados.fechado ? 'bx bx-lock-alt' : 'bx bx-time-five'" aria-hidden="true"></i>
          <span v-if="dados.fechado">
            <strong>Resultado oficial</strong> de {{ periodoLabel }}. As posições deste período não mudam mais.
          </span>
          <span v-else>
            <strong>Parcial ao vivo</strong> de {{ periodoLabel }}<template v-if="dados.atualizado_em">,
            atualizada em {{ fmtAtualizadoEm(dados.atualizado_em) }}</template>. As posições podem mudar até o fechamento.
          </span>
        </div>

        <!-- Sua posição -->
        <section class="card rk-eu" aria-labelledby="rk-eu-titulo">
          <div class="card-body">
            <h2 id="rk-eu-titulo" class="rk-secao-titulo">Sua posição</h2>

            <template v-if="eu && eu.participa">
              <div class="rk-eu-topo">
                <div class="rk-eu-posicao" :class="euPontuou ? classePodio(eu.posicao) : ''">
                  <span class="rk-eu-num">{{ euPontuou ? eu.posicao + "º" : "—" }}</span>
                  <span class="rk-eu-de">de {{ eu.total_participantes }}</span>
                </div>
                <div class="rk-eu-resumo">
                  <div class="rk-eu-pontos">
                    <strong>{{ fmtPontos(eu.pontos) }}</strong> {{ eu.pontos === 1 ? "ponto" : "pontos" }}
                  </div>
                  <div class="rk-eu-selos">
                    <span v-if="euPontuou && eu.premiado" class="rk-selo rk-selo-premio">
                      <i class="bx bx-trophy" aria-hidden="true"></i> Zona de premiação
                    </span>
                    <span v-else-if="euPontuou && eu.no_top" class="rk-selo rk-selo-top">
                      TOP {{ dados.top_n }}
                    </span>
                  </div>
                  <p class="rk-eu-meta mb-0">{{ mensagemProgresso }}</p>
                </div>
              </div>

              <dl class="rk-indicadores">
                <div v-for="c in colunas" :key="c.key" class="rk-ind">
                  <dt :title="c.label">{{ c.curto }} <span class="rk-peso">×{{ fmtPeso(c.peso) }}</span></dt>
                  <dd>{{ fmtPontos(eu.indicadores[c.key]) }}</dd>
                </div>
              </dl>
            </template>

            <EmptyState
              v-else
              size="sm"
              icon="bx bx-user-x"
              title="Você não participa desta trilha"
              :text="eu ? eu.motivo : ''"
            />
          </div>
        </section>

        <!-- Pódio (DOM na ordem 1-2-3 para leitor de tela; a ordem visual 2-1-3 é só CSS) -->
        <section v-if="podio.length" class="card" aria-labelledby="rk-podio-titulo">
          <div class="card-body">
            <h2 id="rk-podio-titulo" class="rk-secao-titulo">Pódio · {{ periodoLabel }}</h2>
            <ol class="rk-podio">
              <li
                v-for="item in podio"
                :key="item.posicao"
                class="rk-podio-item"
                :class="['is-pos-' + item.posicao, { 'is-eu': item.eu }]"
              >
                <span class="sr-only">{{ item.posicao }}º lugar:</span>
                <span class="rk-podio-medalha" aria-hidden="true">{{ MEDALHAS[item.posicao] }}</span>
                <img v-if="item.foto" :src="item.foto" class="rk-avatar rk-avatar-foto" alt="" loading="lazy" />
                <span v-else class="rk-avatar" aria-hidden="true">{{ iniciais(item.nome) }}</span>
                <span class="rk-podio-nome">{{ item.nome }}</span>
                <span v-if="item.eu" class="rk-selo-voce">Você</span>
                <span class="rk-podio-pontos">{{ fmtPontos(item.pontos) }} pts</span>
                <span class="rk-podio-base" aria-hidden="true">{{ item.posicao }}º</span>
              </li>
            </ol>
          </div>
        </section>

        <!-- TOP 20 -->
        <section class="card rk-lista" aria-labelledby="rk-lista-titulo">
          <div class="card-body pb-2">
            <h2 id="rk-lista-titulo" class="rk-secao-titulo">TOP {{ dados.top_n }}</h2>
            <p class="rk-secao-sub mb-0">
              As {{ dados.premiados }} primeiras posições são premiadas ·
              {{ dados.total_participantes }} participantes no período.
            </p>
          </div>

          <EmptyState
            v-if="!top.length"
            icon="bx bx-trophy"
            title="Ninguém pontuou ainda"
            text="Assim que houver produção no período, o ranking aparece aqui."
          />

          <template v-else>
            <!-- Desktop -->
            <div class="table-responsive d-none d-md-block">
              <table class="table table-hover mb-0 rk-tabela">
                <caption class="sr-only">{{ trilha.titulo }} — {{ periodoLabel }}</caption>
                <thead class="thead-light">
                  <tr>
                    <th scope="col" class="text-center rk-th-pos">#</th>
                    <th scope="col">{{ trilha.entidadeLabel }}</th>
                    <th v-if="mostraRegional" scope="col">Regional</th>
                    <th v-for="c in colunas" :key="c.key" scope="col" class="text-center" :title="c.label">
                      {{ c.curto }} <span class="rk-peso">×{{ fmtPeso(c.peso) }}</span>
                    </th>
                    <th scope="col" class="text-right">Pontos</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="item in top"
                    :key="item.posicao"
                    :class="{ 'rk-linha-premio': item.premiado, 'rk-linha-eu': item.eu }"
                  >
                    <td class="text-center rk-td-pos">
                      <span v-if="MEDALHAS[item.posicao]" role="img" :aria-label="item.posicao + 'º lugar'">{{ MEDALHAS[item.posicao] }}</span>
                      <span v-else>{{ item.posicao }}º</span>
                    </td>
                    <td>
                      <div class="rk-pessoa">
                        <img v-if="item.foto" :src="item.foto" class="rk-mini rk-avatar-foto" alt="" loading="lazy" />
                        <span v-else class="rk-mini" aria-hidden="true">{{ iniciais(item.nome) }}</span>
                        <span>
                          <span class="rk-nome">{{ item.nome }}</span>
                          <span v-if="item.eu" class="rk-selo-voce">Você</span>
                        </span>
                      </div>
                    </td>
                    <td v-if="mostraRegional" class="rk-td-regional">{{ item.regional || "—" }}</td>
                    <td v-for="c in colunas" :key="c.key" class="text-center">{{ fmtPontos(item.indicadores[c.key]) }}</td>
                    <td class="text-right rk-td-pontos">{{ fmtPontos(item.pontos) }}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <!-- Celular -->
            <ol class="rk-cards d-md-none">
              <li
                v-for="item in top"
                :key="item.posicao"
                class="rk-card"
                :class="{ 'is-premio': item.premiado, 'is-eu': item.eu }"
              >
                <div class="rk-card-topo">
                  <span class="rk-card-pos" :class="classePodio(item.posicao)">
                    <span class="sr-only">Posição </span>{{ item.posicao }}º
                  </span>
                  <img v-if="item.foto" :src="item.foto" class="rk-mini rk-avatar-foto" alt="" loading="lazy" />
                  <span v-else class="rk-mini" aria-hidden="true">{{ iniciais(item.nome) }}</span>
                  <div class="rk-card-nome">
                    <span class="rk-nome">{{ item.nome }}</span>
                    <span v-if="item.eu" class="rk-selo-voce">Você</span>
                    <span v-if="mostraRegional && item.regional" class="rk-card-regional">{{ item.regional }}</span>
                  </div>
                  <div class="rk-card-pontos">
                    <strong>{{ fmtPontos(item.pontos) }}</strong>
                    <span>pts</span>
                  </div>
                </div>
                <dl class="rk-card-ind">
                  <div v-for="c in colunas" :key="c.key">
                    <dt :title="c.label">{{ c.curto }}</dt>
                    <dd>{{ fmtPontos(item.indicadores[c.key]) }}</dd>
                  </div>
                </dl>
              </li>
            </ol>
          </template>
        </section>
      </template>
    </div>
  </Layout>
</template>

<style scoped src="./ranking.css"></style>
